import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { playNotificationSound } from '../utils/notificationSound';
import messageQueue from '../utils/MessageQueue';
import { API } from '../App';

/**
 * Custom hook for WebSocket connection management
 */
export const useWebSocket = (state, api, user, setUser) => {
  const {
    setWs, setWsConnected, setReconnecting,
    setMessages, setPmMessages, setMembers,
    selectedRoom, rooms, wsRef, reconnectAttemptsRef,
    setActiveTab, setFriendsTabSection,
    setRoomTheme, setSelectedRoom
  } = state;

  const { loadFriends, loadFriendRequests, fetchUnreadPmCount, selectRoom } = api;

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');

    let connectionId = sessionStorage.getItem('ws_connection_id');
    if (!connectionId) {
      connectionId = `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('ws_connection_id', connectionId);
    }

    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/${token}?conn_id=${connectionId}`;
    const websocket = new WebSocket(wsUrl);
    let callRingInterval = null;

    const startCallRing = () => {
      if (callRingInterval) return;
      playNotificationSound();
      callRingInterval = setInterval(() => {
        playNotificationSound();
      }, 1800);
      setTimeout(() => {
        if (callRingInterval) {
          clearInterval(callRingInterval);
          callRingInterval = null;
        }
      }, 15000);
    };

    const stopCallRing = () => {
      if (callRingInterval) {
        clearInterval(callRingInterval);
        callRingInterval = null;
      }
    };

    websocket.onopen = async () => {
      console.log('WebSocket connected');
      reconnectAttemptsRef.current = 0;
      setWsConnected(true);
      setReconnecting(false);

      const heartbeatInterval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);
      websocket.heartbeatInterval = heartbeatInterval;

      const pendingCount = messageQueue.getPendingCount();
      if (pendingCount > 0) {
        toast.info(`Sending ${pendingCount} queued message(s)...`);
        await messageQueue.processQueue(
          websocket,
          () => {},
          (msg, error) => toast.error(`Failed to send message: ${error}`)
        );
        toast.success('All messages sent');
      }

      // Re-sync active room after reconnect so users don't need a manual refresh.
      try {
        const token = localStorage.getItem('token');
        if (token && selectedRoom?.id) {
          websocket.send(JSON.stringify({
            type: 'room_focus',
            room_id: selectedRoom.id,
            is_focused: true
          }));

          const [messagesRes, membersRes, onlineRes] = await Promise.allSettled([
            axios.get(`${API}/rooms/${selectedRoom.id}/messages?limit=20`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API}/rooms/${selectedRoom.id}/members`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API}/rooms/${selectedRoom.id}/online`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);

          if (messagesRes.status === 'fulfilled') {
            const latest = messagesRes.value.data || [];
            setMessages((prev) => {
              const map = new Map();
              [...prev, ...latest].forEach((m) => {
                if (m?.id) map.set(m.id, m);
              });
              return Array.from(map.values()).sort((a, b) =>
                String(a.created_at || '').localeCompare(String(b.created_at || ''))
              );
            });
          }

          if (membersRes.status === 'fulfilled') {
            const membersData = membersRes.value.data || [];
            const onlineUserIds =
              onlineRes.status === 'fulfilled'
                ? (onlineRes.value.data.online_users?.map(u => u.id) || [])
                : [];
            setMembers(
              membersData.map((m) => ({
                ...m,
                online: onlineUserIds.includes(m.user_id || m.user?.id)
              }))
            );
          }
        }

        fetchUnreadPmCount();
      } catch (syncError) {
        console.error('WebSocket post-connect sync failed:', syncError);
      }
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'session_terminated') {
        toast.error(data.message || 'You have been logged out.', { duration: 5000 });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('ws_connection_id');
        setTimeout(() => { window.location.href = '/auth'; }, 2000);
        return;
      }

      if (data.type === 'message' || data.type === 'system') {
        setMessages((prev) => {
          const realMsg = data.message;
          const index = prev.findIndex((m) => m.id === realMsg.tempId);
          if (index !== -1) {
            const next = [...prev];
            next[index] = realMsg;
            return next;
          }
          if (prev.some((m) => m.id === realMsg.id)) return prev;
          return [...prev, realMsg];
        });
      } else if (data.type === 'pm' || data.type === 'pm_sent') {
        window.dispatchEvent(new CustomEvent('pm-live-event', { detail: data }));
        setPmMessages((prev) => {
          const realMsg = data.message;
          const index = prev.findIndex((m) => m.id === realMsg.tempId);
          if (index !== -1) {
            const next = [...prev];
            next[index] = realMsg;
            return next;
          }
          if (prev.some((m) => m.id === realMsg.id)) return prev;
          return [...prev, realMsg];
        });
        if (data.type === 'pm') {
          playNotificationSound();
          toast.info(`New message from ${data.message.sender?.username}`, {
            action: {
              label: 'View',
              onClick: () => {
                setActiveTab('friends');
                setFriendsTabSection('pm-notifications');
              }
            }
          });
          fetchUnreadPmCount();
        }
      } else if (data.type === 'pm_unread_update') {
        fetchUnreadPmCount();
      } else if (data.type === 'mention') {
        playNotificationSound();
        const roomName = data.room_name || data.message?.room_name || 'a room';
        const senderName = data.message?.sender?.username || 'Someone';
        toast.info(`${senderName} mentioned you in ${roomName}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => {
              const roomId = data.room_id || data.message?.room_id;
              const room = rooms.find((r) => r.id === roomId);
              if (room) {
                selectRoom(room);
                setActiveTab('rooms');
              }
            }
          }
        });
      } else if (data.type === 'reply') {
        playNotificationSound();
        toast.info(`${data.message.sender?.username} replied to your message`);
      } else if (data.type === 'presence_update') {
        if (selectedRoom && data.room_id === selectedRoom.id) {
          setMembers((prev) => prev.map((m) => ({
            ...m,
            online: data.online_users?.includes(m.user_id || m.id)
          })));
        }
      } else if (data.type === 'room_theme_update') {
        if (selectedRoom && data.room_id === selectedRoom.id) {
          if (setRoomTheme) setRoomTheme((prev) => ({ ...prev, ...data.theme }));
          if (data.theme.background_url !== undefined && setSelectedRoom) {
            setSelectedRoom((prev) => (prev ? { ...prev, background_url: data.theme.background_url } : prev));
          }
          toast.info('Room theme updated', { duration: 2000 });
        }
      } else if (data.type === 'member_list_update') {
        if (selectedRoom && data.room_id === selectedRoom.id) {
          setMembers(data.members || []);
        }
      } else if (data.type === 'pm_request') {
        playNotificationSound();
        toast.info(`PM request from ${data.from_user?.username}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => {
              setActiveTab('friends');
              setFriendsTabSection('pending');
            }
          }
        });
        loadFriends();
      } else if (data.type === 'friend_request') {
        playNotificationSound();
        toast.info(`Friend request from ${data.from_user?.username}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => {
              setActiveTab('friends');
              setFriendsTabSection('pending');
            }
          }
        });
        loadFriendRequests();
      } else if (data.type === 'user_banned') {
        playNotificationSound();
        toast.error(`You have been banned from ${data.room_name || 'a room'}`, { duration: 8000 });
      } else if (data.type === 'user_kicked') {
        playNotificationSound();
        toast.warning(`You have been kicked from ${data.room_name || 'a room'}`, { duration: 5000 });
      } else if (data.type === 'badge_earned') {
        playNotificationSound();
        toast.success(`Badge Earned: ${data.badge.name}`, { duration: 5000 });
        refreshUserData(setUser);
      } else if (data.type === 'voip_call_invite') {
        startCallRing();
        const callerName = data.from_display_name || data.from_username || 'Unknown user';
        const rejectCall = () => {
          stopCallRing();
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'voip_call_reject',
              call_id: data.call_id,
              recipient_id: data.from_user_id
            }));
          }
          toast.info(`Call rejected: ${callerName}`);
        };

        const acceptCall = async () => {
          stopCallRing();
          try {
            let joinRes;
            try {
              joinRes = await axios.post(`${API}/voip/${data.call_id}/join`);
            } catch (joinError) {
              // Fallback for stale/invalid call_id: re-resolve active call for this PM scope.
              const status = joinError?.response?.status;
              if (status === 404 && data.scope === 'pm') {
                const pmTargetId = data.from_user_id || data.target_id;
                const activeRes = await axios.get(`${API}/voip/active`, {
                  params: { scope: 'pm', target_id: pmTargetId }
                });
                if (activeRes?.data?.active && activeRes?.data?.call?.id) {
                  joinRes = await axios.post(`${API}/voip/${activeRes.data.call.id}/join`);
                  data.call_id = activeRes.data.call.id;
                } else {
                  throw joinError;
                }
              } else {
                throw joinError;
              }
            }
            window.dispatchEvent(new CustomEvent('open-pm-call', {
              detail: {
                friendId: data.from_user_id,
                friendUsername: data.from_username || callerName,
                callId: data.call_id,
                participants: joinRes?.data?.participants || []
              }
            }));
          } catch {
            toast.error('Failed to join call');
          }
        };

        // Do not rely only on toast action/cancel buttons since some skins/extensions hide cancel.
        setTimeout(() => {
          const accepted = window.confirm(`Incoming call from ${callerName}.\nPress OK to accept or Cancel to reject.`);
          if (accepted) {
            acceptCall();
          } else {
            rejectCall();
          }
        }, 50);
      } else if (data.type === 'voip_call_reject') {
        stopCallRing();
        toast.info(`${data.from_display_name || data.from_username || 'User'} rejected the call`);
      } else if (data.type === 'voip_call_end') {
        stopCallRing();
        toast.info('Call ended by host');
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    websocket.onclose = (event) => {
      console.log(`WebSocket disconnected: Code ${event.code}, Reason: ${event.reason}`);
      setWsConnected(false);

      if (websocket.heartbeatInterval) {
        clearInterval(websocket.heartbeatInterval);
      }
      stopCallRing();

      if (event.code === 4001) {
        toast.error('Session expired. Please log in again.', { duration: 5000 });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('ws_connection_id');
        setTimeout(() => {
          if (window.location.pathname !== '/auth') {
            window.location.href = '/auth';
          }
        }, 2000);
        return;
      }

      if (localStorage.getItem('token')) {
        setReconnecting(true);
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket();
        }, delay);
      }
    };

    wsRef.current = websocket;
    setWs(websocket);
  }, [
    setWs, setWsConnected, setReconnecting, setMessages, setPmMessages, setMembers,
    selectedRoom, rooms, wsRef, reconnectAttemptsRef, loadFriends, loadFriendRequests,
    fetchUnreadPmCount, selectRoom, setActiveTab, setFriendsTabSection, setUser
  ]);

  return { connectWebSocket };
};

const refreshUserData = async (setUser) => {
  try {
    const token = localStorage.getItem('token');
    const { data } = await import('axios').then((m) => m.default.get(
      `${API}/auth/me`,
      { headers: { Authorization: `Bearer ${token}` } }
    ));
    setUser(data);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
  }
};

export default useWebSocket;
